import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Administrador. */
export const adminSections = [
  {
    id: "admin-modulos",
    name: "Habilitación de módulos",
    type: "form",
    route: "/admin/module-settings",
    locationPath: "Administrador → Habilitación de Módulos",
    generalDescription: "Activa o desactiva módulos (ventas, maquinaria, restaurantes, auditoría, etc.). El menú y el manual solo muestran módulos activos.",
    prerequisites: [
      "Rol administrador del sistema."
    ],
    interfaceElements: [
      {
        name: "Flags por módulo",
        controlType: "Switch",
        required: false,
        editable: true,
        automatic: false,
        description: "Interruptores ON/OFF.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Guardar",
        controlType: "Botón",
        required: true,
        editable: true,
        automatic: false,
        description: "Persiste en base de datos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise módulos contratados.",
      "Active los necesarios.",
      "Guarde y pida a usuarios recargar la app."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Habilitación de módulos"
      },
      steps: [
        "Revise módulos contratados.",
        "Active los necesarios.",
        "Guarde y pida a usuarios recargar la app."
      ]
    },
    validations: ['Solo administradores pueden guardar; los cambios afectan a todos los usuarios.'],
    expectedResult: ['La configuración queda persistida; los usuarios deben recargar para ver cambios de menú.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "admin-general-settings"
    ],
    observations: [
      "Tras cambios, actualice el manual si agregó pantallas nuevas."
    ],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "admin-general-settings",
    name: "Configuraciones generales",
    type: "form",
    route: "/admin/general-settings",
    locationPath: "Administrador → Configuraciones generales",
    generalDescription: "Parámetros globales de la empresa en la aplicación: datos de razón social, logos, comportamientos transversales según flags disponibles.",
    prerequisites: [
      "Rol administrador."
    ],
    interfaceElements: [
      {
        name: "Datos de empresa",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Nombre, RUC, dirección.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Opciones globales",
        controlType: "Formulario",
        required: false,
        editable: true,
        automatic: false,
        description: "Según implementación.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Guardar",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Aplica cambios.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise cada pestaña.",
      "Ajuste parámetros.",
      "Guarde y verifique en PDFs o reportes."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Configuraciones generales"
      },
      steps: [
        "Revise cada pestaña.",
        "Ajuste parámetros.",
        "Guarde y verifique en PDFs o reportes."
      ]
    },
    validations: ['Solo administradores pueden guardar; los cambios afectan a todos los usuarios.'],
    expectedResult: ['La configuración queda persistida; los usuarios deben recargar para ver cambios de menú.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "admin-modulos"
    ],
    observations: [],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: ROLES.admin
  },

  {
    id: "admin-integracion",
    name: "Integración",
    type: "screen",
    route: "/admin/integration",
    locationPath: "Administrador → Integración",
    generalDescription: "Configuración de API de integración de compras y otros conectores. Consulte INTEGRACION-COMPRAS-API en el repositorio para detalles técnicos.",
    prerequisites: [
      "Rol administrador."
    ],
    interfaceElements: [
      {
        name: "URL y credenciales",
        controlType: "Texto",
        required: false,
        editable: true,
        automatic: false,
        description: "Según conector.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Estado de conexión",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Prueba de ping si existe.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Active módulo de integración en Habilitación de módulos.",
      "Complete parámetros.",
      "Ejecute prueba.",
      "Monitoree logs de actividad."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Integración"
      },
      steps: [
        "Active módulo de integración en Habilitación de módulos.",
        "Complete parámetros.",
        "Ejecute prueba.",
        "Monitoree logs de actividad."
      ]
    },
    validations: ['Solo administradores pueden guardar; los cambios afectan a todos los usuarios.'],
    expectedResult: ['La configuración queda persistida; los usuarios deben recargar para ver cambios de menú.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "admin-modulos"
    ],
    observations: [],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: ROLES.admin
  },

  {
    id: "admin-usuarios",
    name: "Usuarios",
    type: "form",
    route: "/admin/users",
    locationPath: "Administrador → Usuarios",
    generalDescription: "Alta y edición de usuarios: correo, contraseña, rol (administrador, limitado, contador), módulos y alcance por almacén, POS o sede de compra.",
    prerequisites: [
      "Rol administrador."
    ],
    interfaceElements: [
      {
        name: "Correo y contraseña",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Credenciales de login.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Rol",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Define permisos base.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Alcance limitado",
        controlType: "Multiselect",
        required: false,
        editable: true,
        automatic: false,
        description: "Almacenes, POS, sedes.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Cree usuario con correo único.",
      "Asigne rol y módulos.",
      "Restrinja alcance si es limitado.",
      "Entregue credenciales de forma segura."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Usuarios"
      },
      steps: [
        "Cree usuario con correo único.",
        "Asigne rol y módulos.",
        "Restrinja alcance si es limitado.",
        "Entregue credenciales de forma segura."
      ]
    },
    validations: ['Solo administradores pueden guardar; los cambios afectan a todos los usuarios.'],
    expectedResult: ['La configuración queda persistida; los usuarios deben recargar para ver cambios de menú.'],
    commonErrors: [
      {
        error: "No puede iniciar sesión el usuario nuevo",
        cause: "Correo duplicado o módulos sin asignar.",
        solution: "Revise listado y permisos de módulo."
      }
    ],
    relatedFunctions: [
      "general-login"
    ],
    observations: [
      ROLES.limitado,
      ROLES.contador
    ],
    screenshots: [
      SHOTS.formularioCompleto,
      SHOTS.listado
    ],
    roles: ROLES.admin
  }
].map((p) => functionDoc(p));
